import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Plug, Save, RefreshCw, Activity, CheckCircle2, AlertCircle, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function IntegrationsPage() {
  const [ehrConfig, setEhrConfig] = useState({ system: 'generic', protocol: 'fhir', base_url: '', api_key: '', client_id: '', client_secret: '', enabled: false, auto_sync: false, pull_patients: true, pull_doctors: true, pull_appointments: true, sync_fields: [] });
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);

  useEffect(() => {
    Promise.all([
      API.get('/integrations/ehr'),
      API.get('/integrations/ehr/sync-logs'),
      API.get('/patients?limit=100')
    ]).then(([ehr, logs, pts]) => {
      setEhrConfig(ehr.data);
      setSyncLogs(logs.data.logs || []);
      setPatients(pts.data.patients || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await API.put('/integrations/ehr', ehrConfig);
      toast.success('EHR configuration saved');
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const syncPatient = async (patientId) => {
    try {
      const res = await API.post(`/integrations/ehr/sync-patient/${patientId}`);
      toast.success(`EHR sync prepared: ${res.data.is_new_patient ? 'New Patient' : 'Existing Patient'}`);
      const logs = await API.get('/integrations/ehr/sync-logs');
      setSyncLogs(logs.data.logs || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Activity className="w-8 h-8 text-sky-600 animate-pulse-subtle" /></div>;

  const ALL_FIELDS = ['patient_demographics', 'vital_signs', 'allergies', 'medications', 'diagnosis', 'lab_results', 'immunizations'];

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-[1000px]">
      <div>
        <h1 data-testid="integrations-title" className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>EHR / EMR Integration</h1>
        <p className="text-slate-500 mt-1">Connect patient forms and data with your Electronic Health Records system</p>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center"><Plug className="w-5 h-5 text-sky-600" /></div>
            <div><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Integration Configuration</CardTitle><p className="text-sm text-slate-500 mt-0.5">Configure your EHR/EMR/HMIS connection</p></div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Enable Integration</p><p className="text-xs text-slate-500">Activate EHR data sync</p></div>
            <Switch checked={ehrConfig.enabled} onCheckedChange={v => setEhrConfig({ ...ehrConfig, enabled: v })} data-testid="ehr-enabled" />
          </div>

          <div className="space-y-2">
            <Label>EHR / EMR System</Label>
            <Select value={ehrConfig.system || 'generic'} onValueChange={v => setEhrConfig({ ...ehrConfig, system: v })}>
              <SelectTrigger data-testid="ehr-system"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="epic">Epic (FHIR R4)</SelectItem>
                <SelectItem value="cerner">Oracle Cerner (Millennium FHIR)</SelectItem>
                <SelectItem value="meditech">Meditech (Expanse / 6.x)</SelectItem>
                <SelectItem value="allscripts">Allscripts (Sunrise / Professional)</SelectItem>
                <SelectItem value="eclinicalworks">eClinicalWorks (eCW)</SelectItem>
                <SelectItem value="intersystems">InterSystems (TrakCare / HealthShare)</SelectItem>
                <SelectItem value="vista">Vista (Oracle / VA / Gov Sector)</SelectItem>
                <SelectItem value="sap">SAP for Healthcare (IS-H / EMR)</SelectItem>
                <SelectItem value="omnimd">OmniMD</SelectItem>
                <SelectItem value="quadris">Quadris Healthcare Solutions</SelectItem>
                <SelectItem value="nextgen">NextGen Healthcare</SelectItem>
                <SelectItem value="malaffi">Malaffi HIE (UAE - Abu Dhabi)</SelectItem>
                <SelectItem value="nabidh">NABIDH (UAE - Dubai)</SelectItem>
                <SelectItem value="riayati">Riayati (UAE - Federal MoHAP)</SelectItem>
                <SelectItem value="wareed">Wareed (UAE - Abu Dhabi SEHA)</SelectItem>
                <SelectItem value="salamat">Salamat (UAE)</SelectItem>
                <SelectItem value="hakeem">Hakeem (Iraq / GCC)</SelectItem>
                <SelectItem value="healthlink">HealthLink (UAE / KSA Regional)</SelectItem>
                <SelectItem value="bestcare">BestCare (KSA Public Hospitals)</SelectItem>
                <SelectItem value="nana_emr">Nana EMR (KSA)</SelectItem>
                <SelectItem value="tebcare">TebCare (KSA)</SelectItem>
                <SelectItem value="royal_emr">Royal EMR (KSA)</SelectItem>
                <SelectItem value="generic">Generic (Custom REST/FHIR/HL7)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {ehrConfig.system && ehrConfig.system !== 'generic' && (
            <div className="p-3 bg-sky-50 rounded-lg border border-sky-100">
              <p className="text-xs font-medium text-sky-800 mb-1 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {
                { epic: 'Epic Systems', cerner: 'Oracle Cerner (Millennium)', meditech: 'Meditech Expanse / 6.x', allscripts: 'Allscripts Sunrise / Professional', eclinicalworks: 'eClinicalWorks (eCW)', intersystems: 'InterSystems TrakCare / HealthShare', vista: 'Vista (Oracle / VA Gov Sector)', sap: 'SAP for Healthcare (IS-H)', omnimd: 'OmniMD Cloud EMR', quadris: 'Quadris Healthcare Solutions', nextgen: 'NextGen Healthcare', malaffi: 'Malaffi Health Information Exchange', nabidh: 'NABIDH - Dubai Health', riayati: 'Riayati - Federal MoHAP', wareed: 'Wareed - Abu Dhabi SEHA', salamat: 'Salamat Health Platform', hakeem: 'Hakeem (Iraq / GCC)', healthlink: 'HealthLink Regional Platform', bestcare: 'BestCare (KSA Public)', nana_emr: 'Nana EMR (KSA)', tebcare: 'TebCare (KSA)', royal_emr: 'Royal EMR (KSA)' }[ehrConfig.system]
              }</p>
              <p className="text-[11px] text-sky-600">{
                { epic: 'Uses FHIR R4 with OAuth 2.0. Requires Epic App Orchard registration.', cerner: 'Uses Cerner FHIR R4 API. Requires Cerner Developer account and Millennium integration.', meditech: 'Supports HL7 v2.x, FHIR R4, and Meditech APIs. Expanse version supports modern REST.', allscripts: 'FHIR R4 and HL7 v2.x support. Unity/Professional APIs available. Requires developer portal access.', eclinicalworks: 'FHIR R4 APIs with eCW cloud integration. Supports V11+ REST endpoints.', intersystems: 'Supports HL7 FHIR, HL7 v2, and REST APIs. Configure based on your TrakCare version.', vista: 'VA-standard HL7 and FHIR interfaces. Used in government/military healthcare facilities.', sap: 'SAP IS-H module with HL7 integration. Supports FHIR via SAP BTP Health connectors.', omnimd: 'Cloud-based REST APIs with FHIR support. SaaS integration model.', quadris: 'Custom REST integration with HL7 message support. Regional GCC deployment.', nextgen: 'FHIR R4 and HL7 v2.x. NextGen Connect integration engine available.', malaffi: 'Abu Dhabi HIE platform. FHIR R4 with UAE Patient ID integration.', nabidh: 'Dubai Health Authority. HL7 FHIR with Emirates ID mapping.', riayati: 'Federal MoHAP system. HL7 v2.x with CDA document exchange.', wareed: 'SEHA Abu Dhabi integrated system. Uses HL7 v2.x and custom REST APIs.', salamat: 'UAE health platform. REST API with patient demographic sync.', hakeem: 'Iraq national health system. HL7 v2.x with regional adaptations. Used in GCC discussions.', healthlink: 'Regional HIE platform connecting UAE/KSA facilities. FHIR and HL7 support.', bestcare: 'Korean-origin EMR widely used in KSA public hospitals. HL7 v2.x integration.', nana_emr: 'Saudi local EMR vendor. REST API and HL7 v2.x support.', tebcare: 'KSA healthcare platform. Custom REST APIs with Arabic language support.', royal_emr: 'KSA EMR vendor for public sector. HL7 v2.x and custom integration.' }[ehrConfig.system]
              }</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Protocol</Label>
              <Select value={ehrConfig.protocol} onValueChange={v => setEhrConfig({ ...ehrConfig, protocol: v })}>
                <SelectTrigger data-testid="ehr-protocol"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fhir">FHIR (HL7 FHIR R4)</SelectItem>
                  <SelectItem value="hl7">HL7 v2.x</SelectItem>
                  <SelectItem value="rest">Custom REST API</SelectItem>
                  <SelectItem value="cda">CDA (Clinical Document Architecture)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Base URL / Endpoint</Label>
              <Input data-testid="ehr-url" value={ehrConfig.base_url || ''} onChange={e => setEhrConfig({ ...ehrConfig, base_url: e.target.value })} placeholder="https://ehr.hospital.com/api/fhir" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client ID / API Key</Label>
              <Input data-testid="ehr-client-id" type="password" value={ehrConfig.client_id || ehrConfig.api_key || ''} onChange={e => setEhrConfig({ ...ehrConfig, client_id: e.target.value, api_key: e.target.value })} placeholder="Client ID or API Key" />
            </div>
            <div className="space-y-2">
              <Label>Client Secret</Label>
              <Input data-testid="ehr-client-secret" type="password" value={ehrConfig.client_secret || ''} onChange={e => setEhrConfig({ ...ehrConfig, client_secret: e.target.value })} placeholder="Client Secret (OAuth)" />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Data Pull Configuration</Label>
            <div className="grid grid-cols-3 gap-4">
              {[{ key: 'pull_patients', label: 'Pull Patients' }, { key: 'pull_doctors', label: 'Pull Doctors/Practitioners' }, { key: 'pull_appointments', label: 'Pull Appointments' }].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-xs font-medium">{label}</span>
                  <Switch checked={ehrConfig[key] !== false} onCheckedChange={v => setEhrConfig({ ...ehrConfig, [key]: v })} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Auto-Sync</p><p className="text-xs text-slate-500">Automatically push data when forms are submitted</p></div>
            <Switch checked={ehrConfig.auto_sync} onCheckedChange={v => setEhrConfig({ ...ehrConfig, auto_sync: v })} data-testid="ehr-auto-sync" />
          </div>

          <div className="space-y-2">
            <Label>Sync Fields</Label>
            <div className="flex flex-wrap gap-2">
              {ALL_FIELDS.map(field => {
                const active = (ehrConfig.sync_fields || []).includes(field);
                return (
                  <Badge key={field} variant={active ? 'default' : 'secondary'}
                    className={`cursor-pointer transition-all ${active ? 'bg-sky-100 text-sky-700 hover:bg-sky-200' : 'hover:bg-slate-200'}`}
                    onClick={() => {
                      const fields = ehrConfig.sync_fields || [];
                      setEhrConfig({ ...ehrConfig, sync_fields: active ? fields.filter(f => f !== field) : [...fields, field] });
                    }}>
                    {field.replace(/_/g, ' ')}
                  </Badge>
                );
              })}
            </div>
          </div>

          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800"><strong>Note:</strong> This integration module is optional and on-demand. The system identifies whether a patient is new or existing. Enabling this feature requires your EHR system to support the selected protocol. Contact your IT team for the API credentials.</p>
          </div>

          <Button data-testid="save-ehr-config" onClick={saveConfig} disabled={saving} className="bg-sky-600 hover:bg-sky-700"><Save className="w-4 h-4 mr-2" /> Save Configuration</Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Manual Patient Sync</CardTitle>
          <p className="text-sm text-slate-500">Push individual patient records to EHR</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {patients.slice(0, 20).map(p => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium">{p.name}</span>
                <Button size="sm" variant="outline" onClick={() => syncPatient(p.id)} data-testid={`sync-patient-${p.id}`}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {syncLogs.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Sync History</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Patient</TableHead><TableHead>Protocol</TableHead><TableHead>Status</TableHead><TableHead>New Patient?</TableHead><TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {syncLogs.slice(0, 20).map(log => (
                  <TableRow key={log.id}>
                    <TableCell className="font-medium">{log.patient_id?.slice(0, 8)}...</TableCell>
                    <TableCell><Badge variant="secondary" className="uppercase text-xs">{log.protocol}</Badge></TableCell>
                    <TableCell><Badge className={log.status === 'prepared' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}>{log.status}</Badge></TableCell>
                    <TableCell>{log.is_new_patient ? <Badge className="bg-amber-100 text-amber-700">New</Badge> : <Badge variant="secondary">Existing</Badge>}</TableCell>
                    <TableCell className="text-sm text-slate-500">{new Date(log.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
